import { faUserPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { Component } from "react";
import { ButtonGroup, ButtonToolbar, Pagination } from "react-bootstrap";
import { FormattedMessage, injectIntl } from "react-intl";
import { USER_EDITOR } from "../../../common/constant";
import {
    ACTIVE_USERS_ONLY,
    CREATE_NEW_USER_DIALOG,
    UNNOTIFIED_USERS_ONLY
} from "../../../ducks/userManagement";
import Checkbox from "../../common/Checkbox";
import StandardView from "../../common/StandardView";
import { resolveRoleDisplayName } from "../roleManagement/RoleNameResolver";
import ChangeUserStatusDialog from "./ChangeUserStatusDialog";
import CreateEditUserDialog from "./CreateEditUserDialog";
import UserManagementTable from "./UserManagementTable";

class UserManagement extends Component {
  constructor(props) {
    super(props);
    this.onNewUser = this.onNewUser.bind(this);
    this.onChangeActiveOnly = this.onChangeActiveOnly.bind(this);
    this.handleSearchChange = this.handleSearchChange.bind(this);
    this.state = {
      currentPage: 1,
      pageSize: 100,
      searchQuery: ""
    };
  }

  componentDidMount() {
    const { loadUsers, loadAvailableRoles, loadMasterData, clearMessages } = this.props;
    //We are triggering 'ducks' function, that will make a rest call and get data from the server.
    clearMessages();
    loadUsers(false);
    loadAvailableRoles();
    loadMasterData();
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevProps.users !== this.props.users || prevState.searchQuery !== this.state.searchQuery) {
      const filteredUsers = this.applySearch(this.props.users, this.props.usersParticipations);
      const totalPages = Math.max(1, Math.ceil(filteredUsers.length / this.state.pageSize));
      if (this.state.currentPage > totalPages) {
        this.setState({ currentPage: totalPages });
      }
    }
  }

  //opens popup for a new user
  onNewUser() {
    const { propertyChange, cleanUserData } = this.props;
    cleanUserData();
    propertyChange(CREATE_NEW_USER_DIALOG, true);
  }

  onChangeActiveOnly(e) {
    const { propertyChange, loadUsers } = this.props;
    propertyChange(ACTIVE_USERS_ONLY, e);
    this.setState({ currentPage: 1 }, () => loadUsers());
  }

  onChangeUnnotifiedUsersOnly(e) {
    const { propertyChange, loadUsers } = this.props;
    propertyChange(UNNOTIFIED_USERS_ONLY, e);
    this.setState({ currentPage: 1 }, () => loadUsers());
  }

  handleSearchChange(event) {
    this.setState({
      searchQuery: event.target.value,
      currentPage: 1
    });
  }

  applySearch(users = [], usersParticipations = {}) {
    const q = this.state.searchQuery.trim().toLowerCase();
    if (!q) {
      return users;
    }

    return users.filter((user) => {
      const roleNames = user.roles
        ?.map((role) => `${role.name} ${resolveRoleDisplayName(role, this.props.locale, this.props.intl)}`)
        .join(" ") || "";
      const unitNames =
        usersParticipations[user.username]
          ?.map((participation) => `${participation.unit?.name || ""} ${participation.unit?.foreignId || ""}`)
          .join(" ") || "";
      const activeLabel = this.props.intl.formatMessage({ id: user.active ? "app.common.Yes" : "app.common.No" });
      const searchableText = [
        user.username,
        user.firstname,
        user.lastname,
        roleNames,
        unitNames,
        activeLabel
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchableText.includes(q);
    });
  }

  getPagedUsers(users) {
    const startIndex = (this.state.currentPage - 1) * this.state.pageSize;
    return users.slice(startIndex, startIndex + this.state.pageSize);
  }

  goToPage(page, totalItems) {
    const totalPages = Math.max(1, Math.ceil(totalItems / this.state.pageSize));
    window.scrollTo({ top: 0, behavior: "smooth" });
    if (page >= 1 && page <= totalPages) {
      this.setState({ currentPage: page });
    }
  }

  renderPaginationItems(totalItems) {
    const totalPages = Math.ceil(totalItems / this.state.pageSize);
    const { currentPage } = this.state;
    const items = [];

    if (totalPages <= 9) {
      for (let i = 1; i <= totalPages; i++) {
        items.push(this.renderPaginationItem(i, totalItems));
      }
      return items;
    }

    if (currentPage <= 5) {
      for (let i = 1; i <= 6; i++) {
        items.push(this.renderPaginationItem(i, totalItems));
      }
      items.push(<Pagination.Ellipsis key="right-ellipsis" disabled />);
      for (let i = totalPages - 2; i <= totalPages; i++) {
        items.push(this.renderPaginationItem(i, totalItems));
      }
      return items;
    }

    if (currentPage >= totalPages - 4) {
      for (let i = 1; i <= 3; i++) {
        items.push(this.renderPaginationItem(i, totalItems));
      }
      items.push(<Pagination.Ellipsis key="left-ellipsis" disabled />);
      for (let i = totalPages - 5; i <= totalPages; i++) {
        items.push(this.renderPaginationItem(i, totalItems));
      }
      return items;
    }

    for (let i = 1; i <= 3; i++) {
      items.push(this.renderPaginationItem(i, totalItems));
    }
    items.push(<Pagination.Ellipsis key="middle-left" disabled />);
    for (let i = currentPage - 1; i <= currentPage + 1; i++) {
      items.push(this.renderPaginationItem(i, totalItems));
    }
    items.push(<Pagination.Ellipsis key="middle-right" disabled />);
    for (let i = totalPages - 2; i <= totalPages; i++) {
      items.push(this.renderPaginationItem(i, totalItems));
    }

    return items;
  }

  renderPaginationItem(page, totalItems) {
    return (
      <Pagination.Item
        key={page}
        active={page === this.state.currentPage}
        onClick={() => this.goToPage(page, totalItems)}
        aria-current={page === this.state.currentPage ? "page" : undefined}
      >
        {page}
      </Pagination.Item>
    );
  }

  renderPagination(totalItems) {
    const totalPages = Math.ceil(totalItems / this.state.pageSize);
    if (totalItems <= this.state.pageSize) {
      return null;
    }

    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <span>
          <FormattedMessage
            id="pagination.showing"
            values={{
              current: this.state.currentPage,
              total: Math.max(1, totalPages)
            }}
          />
        </span>
        <Pagination className="mb-0" size="sm">
          <Pagination.First
            onClick={() => this.goToPage(1, totalItems)}
            disabled={this.state.currentPage === 1}
          />
          <Pagination.Prev
            onClick={() => this.goToPage(this.state.currentPage - 1, totalItems)}
            disabled={this.state.currentPage === 1}
          />
          {this.renderPaginationItems(totalItems)}
          <Pagination.Next
            onClick={() => this.goToPage(this.state.currentPage + 1, totalItems)}
            disabled={this.state.currentPage >= totalPages}
          />
          <Pagination.Last
            onClick={() => this.goToPage(totalPages, totalItems)}
            disabled={this.state.currentPage >= totalPages}
          />
        </Pagination>
      </div>
    );
  }

  render() {
    const {
      users,
      usersParticipations = {},
      sortUserTableBy,
      sortUserTableOrder,
      editUser,
      propertyChange,
      username,
      active,
      loadUsers,
      changeUserStatusDialog,
      changeSortingProperties,
      prepareUserDataForEditing,
      loaded,
      locale,
      activeOnly,
      unnotifiedUsersOnly,
      sendUserAccountNotification,
      intl
    } = this.props;
    const filteredUsers = this.applySearch(users, usersParticipations);
    const pagedUsers = this.getPagedUsers(filteredUsers);
    const showSearch = users.length > 0 || this.state.searchQuery;

    return (
      <StandardView
        id="userManagement"
        titleKey="user.management.page.title"
        actions={[
          {
            roles: [USER_EDITOR],
            labelKey: "user.management.new.user",
            onClick: this.onNewUser,
            icon: <FontAwesomeIcon icon={faUserPlus} className="me-2" />
          }
        ]}
      >
        <CreateEditUserDialog {...this.props} />
        <ChangeUserStatusDialog
          username={username}
          active={active}
          propertyChange={propertyChange}
          editUser={editUser}
          changeUserStatusDialog={changeUserStatusDialog}
        />
        <ButtonToolbar className="mb-2">
          <ButtonGroup className="gap-lg align-items-center">
            <span>
              {filteredUsers.length} <FormattedMessage id="user.management.users.per.page" />
            </span>
            <Checkbox
              name="activeOnly"
              value={activeOnly.toString()}
              checked={activeOnly}
              type="checkbox"
              onChange={(e) => this.onChangeActiveOnly(!(e.target.value === "true"))}
            >
              <FormattedMessage id="user.management.table.only.active.users" />
            </Checkbox>
            <Checkbox
              name="unnotifiedUsersOnly"
              value={unnotifiedUsersOnly.toString()}
              checked={unnotifiedUsersOnly}
              disabled
              type="checkbox"
              onChange={(e) => this.onChangeUnnotifiedUsersOnly(!(e.target.value === "true"))}
            >
              <FormattedMessage id="user.management.table.only.unnotified.users" />
            </Checkbox>
            {showSearch && (
              <div className="m-0 flex-center gap-sm mb-1">
                <input
                  type="search"
                  className="form-control w-fit"
                  value={this.state.searchQuery}
                  onChange={this.handleSearchChange}
                  placeholder={intl.formatMessage({
                    id: "user.management.search.placeholder",
                    defaultMessage: "Suche in Benutzerverwaltung..."
                  })}
                  aria-label={intl.formatMessage({
                    id: "user.management.search.aria",
                    defaultMessage: "Search in user management"
                  })}
                />
              </div>
            )}
          </ButtonGroup>
        </ButtonToolbar>
        {this.renderPagination(filteredUsers.length)}
        <UserManagementTable
          users={pagedUsers}
          usersParticipations={usersParticipations}
          sortUserTableBy={sortUserTableBy}
          sortUserTableOrder={sortUserTableOrder}
          loaded={loaded}
          prepareUserDataForEditing={prepareUserDataForEditing}
          propertyChange={propertyChange}
          loadUsers={loadUsers}
          changeSortingProperties={changeSortingProperties}
          locale={locale}
          onSort={() => this.setState({ currentPage: 1 })}
          sendUserAccountNotification={sendUserAccountNotification}
        />
        {this.renderPagination(filteredUsers.length)}
      </StandardView>
    );
  }
}

UserManagement.propTypes = {
  loadUsers: PropTypes.func,
  propertyChange: PropTypes.func,
  editUser: PropTypes.func,
  functionSwitchPage: PropTypes.func,
  users: PropTypes.array,
  usersParticipations: PropTypes.object,
  createNewUserDialog: PropTypes.bool,
  editUserDialog: PropTypes.bool,
  username: PropTypes.string,
  sortUserTableBy: PropTypes.string,
  sortUserTableOrder: PropTypes.string,
  active: PropTypes.bool,
  currentPasswordIsValid: PropTypes.bool,
  userAlreadyExists: PropTypes.bool,
  localRoles: PropTypes.array,
  prepareUserDataForEditing: PropTypes.func,
  onHandlePrev: PropTypes.func,
  onHandleNext: PropTypes.func,
  onHandleLast: PropTypes.func,
  onHandleFirst: PropTypes.func,
  createNewUser: PropTypes.func,
  changeUserStatusDialog: PropTypes.bool,
  cleanUserData: PropTypes.func,
  roles: PropTypes.array,
  loadAvailableRoles: PropTypes.func,
  changeSortingProperties: PropTypes.func,
  userTablePage: PropTypes.object,
  loaded: PropTypes.bool,
  fundingAreaList: PropTypes.arrayOf(PropTypes.object),
  loadMasterData: PropTypes.func,
  setFilters: PropTypes.func,
  roleList: PropTypes.array,
  userParticipations: PropTypes.array,
  locale: PropTypes.string.isRequired,
  sendUserNotification: PropTypes.bool,
  unnotifiedUsersOnly: PropTypes.bool,
  sendUserAccountNotification: PropTypes.func.isRequired
};

export default injectIntl(UserManagement);
